import { get, map, orderBy, reduce } from 'lodash';
import { useDispatch } from 'react-redux';
import { useEffect, useMemo } from 'react';

import agentsResource from 'src/actions/agent/agentsResource';
import ComplianceLogEvent from 'src/actions/compliance/complianceEvent';
import complianceEventsResource from 'src/actions/compliance/complianceEventsResource';
import useResource, {
  allSucceeded,
  getConsolidatedState,
  someFailed,
  useResourceValues,
} from 'src/presentation/hooks/useResource';

const useComplianceEvents = () => {
  const dispatch = useDispatch();

  const { values: events, loadingState: eventsLoadingState } =
    useResourceValues<ComplianceLogEvent>(complianceEventsResource);
  const { values: agents = [], loadingState: agentsLoadingState } = useResource(agentsResource);

  const agentsMap = useMemo(() => reduce(agents, (acc, agent) => ({ ...acc, [agent.id]: agent }), {}), [agents]);

  useEffect(() => {
    dispatch(complianceEventsResource.effects.fetch({}));
  }, [dispatch]);

  const logWithAgentsOrderByCreatedAtDesc = useMemo(() => {
    if (!allSucceeded(eventsLoadingState, agentsLoadingState) && !someFailed(eventsLoadingState, agentsLoadingState)) {
      return [];
    }

    return orderBy(
      map(events, (event: ComplianceLogEvent) => {
        const actionByUser =
          event.actionBy.type.toUpperCase() === 'AGENT'
            ? get(agentsMap, `[${get(event, 'actionBy.userId')}].name`, '')
            : 'System';
        return { ...event, actionByUser };
      }),
      ['createdAt'],
      ['desc']
    );
  }, [events, agentsMap, eventsLoadingState, agentsLoadingState]);

  return {
    events: logWithAgentsOrderByCreatedAtDesc,
    loadingState: getConsolidatedState([eventsLoadingState, agentsLoadingState]),
  };
};

export default useComplianceEvents;
